import React from 'react';
import Legend from './Legend';

// Filter types and state definitions
export type FilterType = 'temperature' | 'pressure' | 'humidity' | 'precipitation' | 'SM' | 'SMRZ';

export interface ParameterFilter {
  type: FilterType;
  minValue: number | null;
  maxValue: number | null;
  active: boolean;
}

export interface FilterState {
  filters: Record<FilterType, ParameterFilter>;
  activeFilters: FilterType[];
}

export const DEFAULT_FILTER_VALUES: Record<FilterType, { min: number; max: number }> = {
  temperature: { min: -10, max: 50 },
  pressure: { min: 95, max: 105 },
  humidity: { min: 0, max: 100 },
  precipitation: { min: 0, max: 200 },
  SM: { min: 0, max: 1 },
  SMRZ: { min: 0, max: 1 },
};

interface FilterPanelProps {
  filter: FilterState;
  onFilterChange: (filter: FilterState) => void;
  onClose?: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({ filter, onFilterChange, onClose }) => {
  const toggleFilter = (type: FilterType) => {
    const updatedFilters = { ...filter.filters };
    updatedFilters[type] = {
      ...updatedFilters[type],
      active: !updatedFilters[type].active
    };

    let newActiveFilters = [...filter.activeFilters];
    if (updatedFilters[type].active) {
      if (!newActiveFilters.includes(type)) {
        newActiveFilters.push(type);
      }
    } else {
      newActiveFilters = newActiveFilters.filter(f => f !== type);
    }

    onFilterChange({
      filters: updatedFilters,
      activeFilters: newActiveFilters
    });
  };

  const handleMinValueChange = (type: FilterType, value: string) => {
    const minValue = value ? parseFloat(value) : null;
    const updatedFilters = { ...filter.filters };
    updatedFilters[type] = {
      ...updatedFilters[type],
      minValue
    };

    onFilterChange({
      ...filter,
      filters: updatedFilters
    });
  };

  const handleMaxValueChange = (type: FilterType, value: string) => {
    const maxValue = value ? parseFloat(value) : null;
    const updatedFilters = { ...filter.filters };
    updatedFilters[type] = {
      ...updatedFilters[type],
      maxValue
    };

    onFilterChange({
      ...filter,
      filters: updatedFilters
    });
  };

  const clearAllFilters = () => {
    const resetFilters: Record<FilterType, ParameterFilter> = {
      temperature: { type: 'temperature', minValue: null, maxValue: null, active: false },
      pressure: { type: 'pressure', minValue: null, maxValue: null, active: false },
      humidity: { type: 'humidity', minValue: null, maxValue: null, active: false },
      precipitation: { type: 'precipitation', minValue: null, maxValue: null, active: false },
      SM: { type: 'SM', minValue: null, maxValue: null, active: false },
      SMRZ: { type: 'SMRZ', minValue: null, maxValue: null, active: false },
    };

    onFilterChange({
      filters: resetFilters,
      activeFilters: []
    });
  };

  // Helper function to get parameter label
  const getParameterLabel = (type: FilterType): string => {
    switch(type) {
      case 'temperature': return 'Temperature (°C)';
      case 'pressure': return 'Pressure (kPa)';
      case 'humidity': return 'Humidity (%)';
      case 'precipitation': return 'Precipitation (mm)';
      case 'SM': return 'Soil Moisture (Surface)';
      case 'SMRZ': return 'Soil Moisture (Rootzone)';
    }
  };

  return (
    <>
      <div className="filter-sidebar-header">
        <h3>Filter Data</h3>
        {onClose && (
          <button className="filter-sidebar-close" onClick={onClose} title="Close">×</button>
        )}
      </div>
      <div className="filter-instruction">
        <p>Select parameters to filter the meteorological data</p>
      </div>
      {Object.entries(filter.filters).map(([type, paramFilter]) => (
        <div key={type} className="parameter-filter">
          <div className="filter-header">
            <label className="checkbox-label">
              <input 
                type="checkbox" 
                checked={paramFilter.active}
                onChange={() => toggleFilter(type as FilterType)}
              />
              <span>{getParameterLabel(type as FilterType)}</span>
            </label>
          </div>
          {paramFilter.active && (
            <div className="filter-range">
              <div className="filter-input">
                <label>Min:</label>
                <input
                  type="number"
                  value={paramFilter.minValue !== null ? paramFilter.minValue : ''}
                  onChange={(e) => handleMinValueChange(type as FilterType, e.target.value)}
                  placeholder={`Min (${DEFAULT_FILTER_VALUES[type as FilterType].min})`}
                />
              </div>
              <div className="filter-input">
                <label>Max:</label>
                <input
                  type="number"
                  value={paramFilter.maxValue !== null ? paramFilter.maxValue : ''}
                  onChange={(e) => handleMaxValueChange(type as FilterType, e.target.value)}
                  placeholder={`Max (${DEFAULT_FILTER_VALUES[type as FilterType].max})`}
                />
              </div>
            </div>
          )}
        </div>
      ))}
      {filter.activeFilters.length === 0 && (
        <div className="no-filters-message">
          <p>No filters selected. All data points will be shown.</p>
        </div>
      )}
      <Legend filter={filter} />
    </>
  );
};

export default FilterPanel; 